import { useState, useEffect, useRef } from 'react';
import { Lock, Loader2, Eye, EyeOff, Mail, KeyRound, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { useVault } from './VaultContext';
import { useAuth } from '@/auth';
import { toast } from 'sonner';
import { BiometricUnlock } from './BiometricUnlock';
import { BiometricSetup } from './BiometricSetup';

interface VaultUnlockSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const REMEMBERED_EMAIL_KEY = 'vault_email';

export function VaultUnlockSheet({ open, onOpenChange }: VaultUnlockSheetProps) {
  const {
    unlock,
    submitTwoFactor,
    cancelTwoFactor,
    isLoading,
    error,
    requiresTwoFactor,
    isUnlocked,
    isBiometricAvailable,
    isBiometricEnabled,
    biometricDiagnostics,
  } = useVault();
  const { user } = useAuth();

  const [email, setEmail] = useState('');
  const [masterPassword, setMasterPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberEmail, setRememberEmail] = useState(true);
  // 2FA state
  const [totpCode, setTotpCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  // Biometric setup state
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState<{ email: string; password: string } | null>(null);
  // Track if we should show biometric setup after unlock
  const wasUnlockedRef = useRef(false);

  // Load email: first from localStorage, then from Keycloak
  useEffect(() => {
    const saved = localStorage.getItem(REMEMBERED_EMAIL_KEY);
    if (saved) {
      setEmail(saved);
    } else if (user?.email) {
      setEmail(user.email);
    }
  }, [user?.email]);

  // Show biometric setup after successful unlock (if biometric is available but not enabled)
  useEffect(() => {
    if (isUnlocked && !wasUnlockedRef.current && pendingCredentials) {
      wasUnlockedRef.current = true;
      if (isBiometricAvailable && !isBiometricEnabled) {
        setShowBiometricSetup(true);
      }
    }
    if (!isUnlocked) {
      wasUnlockedRef.current = false;
    }
  }, [isUnlocked, pendingCredentials, isBiometricAvailable, isBiometricEnabled]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Handle 2FA submission
    if (requiresTwoFactor) {
      if (!totpCode || totpCode.length !== 6) return;
      await submitTwoFactor(totpCode, rememberDevice);
      // Clear TOTP code after submission
      setTotpCode('');
      return;
    }

    // Handle initial login
    if (!email || !masterPassword) return;

    // Save email if remember is checked
    if (rememberEmail) {
      localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
    } else {
      localStorage.removeItem(REMEMBERED_EMAIL_KEY);
    }

    // Save credentials for potential biometric setup
    setPendingCredentials({ email, password: masterPassword });

    await unlock(email, masterPassword);

    // Clear password on success (but not on error)
    if (!error) {
      setMasterPassword('');
    }
  };

  const handleBack = () => {
    cancelTwoFactor();
    setTotpCode('');
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setMasterPassword('');
      setTotpCode('');
      setPendingCredentials(null);
      setShowBiometricSetup(false);
      if (requiresTwoFactor) {
        cancelTwoFactor();
      }
    }
    onOpenChange(newOpen);
  };

  const handleBiometricSetupClose = () => {
    setShowBiometricSetup(false);
    setPendingCredentials(null);
  };

  return (
    <>
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="bottom" className="h-[70vh]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {requiresTwoFactor ? (
              <>
                <KeyRound className="h-5 w-5" />
                Двухфакторная аутентификация
              </>
            ) : (
              <>
                <Lock className="h-5 w-5" />
                Корпоративные пароли
              </>
            )}
          </SheetTitle>
          <SheetDescription>
            {requiresTwoFactor
              ? 'Введите код из приложения аутентификатора'
              : 'Введите master password для доступа к паролям'}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4 overflow-y-auto max-h-[calc(70vh-100px)]" autoComplete="off">
          {requiresTwoFactor ? (
            <>
              {/* Back button */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mb-2 -ml-2"
                onClick={handleBack}
                disabled={isLoading}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Назад
              </Button>

              {/* TOTP Code */}
              <div className="space-y-2">
                <Label htmlFor="vault-totp">Код подтверждения</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="vault-totp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    className="pl-10 text-center text-2xl tracking-widest font-mono"
                    autoComplete="one-time-code"
                    autoFocus
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Remember device */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember-device"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                  disabled={isLoading}
                />
                <Label htmlFor="remember-device" className="text-sm text-muted-foreground">
                  Запомнить устройство на 30 дней
                </Label>
              </div>
            </>
          ) : (
            <>
              {/* Biometric unlock (if enabled) */}
              {isBiometricEnabled && (
                <>
                  <BiometricUnlock />

                  {/* Separator */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">
                        или введите пароль
                      </span>
                    </div>
                  </div>
                </>
              )}

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="vault-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="vault-email"
                    type="email"
                    placeholder="user@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    autoComplete="off"
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Master Password */}
              <div className="space-y-2">
                <Label htmlFor="vault-secret">Master Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="vault-secret"
                    name="vault-secret"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Master Password"
                    value={masterPassword}
                    onChange={(e) => setMasterPassword(e.target.value)}
                    className="pl-10 pr-10"
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    disabled={isLoading}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Remember email */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember-email"
                  checked={rememberEmail}
                  onChange={(e) => setRememberEmail(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="remember-email" className="text-sm text-muted-foreground">
                  Запомнить email
                </Label>
              </div>
            </>
          )}

          {/* Biometric diagnostics when unavailable */}
          {!requiresTwoFactor && !isBiometricEnabled && !isBiometricAvailable && biometricDiagnostics && (
            <div
              className="p-3 rounded-lg bg-amber-50 text-amber-800 text-xs space-y-1 select-none"
              onContextMenu={(e) => e.preventDefault()}
              onTouchStart={(e) => {
                const timer = setTimeout(() => {
                  const diag = [
                    `WebAuthn API: ${biometricDiagnostics.webAuthnSupported}`,
                    `Platform Authenticator: ${biometricDiagnostics.platformAuthenticatorAvailable}`,
                    `Error: ${biometricDiagnostics.error ?? 'none'}`,
                    `UA: ${navigator.userAgent}`,
                  ].join('\n');
                  navigator.clipboard.writeText(diag).then(
                    () => toast.success('Диагностика скопирована'),
                    () => toast.error('Не удалось скопировать'),
                  );
                }, 500);
                const el = e.currentTarget;
                const clear = () => { clearTimeout(timer); el.removeEventListener('touchend', clear); el.removeEventListener('touchmove', clear); };
                el.addEventListener('touchend', clear, { once: true });
                el.addEventListener('touchmove', clear, { once: true });
              }}
            >
              <div className="font-medium">FaceID недоступен для автоматической разблокировки</div>
              <div>WebAuthn API: {biometricDiagnostics.webAuthnSupported ? '✓' : '✗'}</div>
              <div>Platform Authenticator: {biometricDiagnostics.platformAuthenticatorAvailable === null ? '—' : biometricDiagnostics.platformAuthenticatorAvailable ? '✓' : '✗'}</div>
              {biometricDiagnostics.error && <div>Ошибка: {biometricDiagnostics.error}</div>}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}

          {/* Submit */}
          <Button
            type="submit"
            className="w-full"
            disabled={
              isLoading ||
              (requiresTwoFactor ? totpCode.length !== 6 : !email || !masterPassword)
            }
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {requiresTwoFactor ? 'Проверка...' : 'Разблокировка...'}
              </>
            ) : requiresTwoFactor ? (
              <>
                <KeyRound className="h-4 w-4 mr-2" />
                Подтвердить
              </>
            ) : (
              <>
                <Lock className="h-4 w-4 mr-2" />
                Разблокировать
              </>
            )}
          </Button>

          {/* Info */}
          <p className="text-xs text-muted-foreground text-center">
            {requiresTwoFactor
              ? 'Введите 6-значный код из Google Authenticator или аналогичного приложения'
              : 'Сессия автоматически завершится через 5 минут или при переключении вкладки'}
          </p>
        </form>
      </SheetContent>
    </Sheet>

    {/* Biometric setup dialog (shown after successful unlock) */}
    {pendingCredentials && (
      <BiometricSetup
        open={showBiometricSetup}
        onClose={handleBiometricSetupClose}
        email={pendingCredentials.email}
        password={pendingCredentials.password}
      />
    )}
  </>
  );
}
